// Faction registry (D1): every faction-variant number and def pointer the sim reads.
// Balance passes for faction identity touch this file, not TUNING.

import type { FactionId, OneVsOneMapId, TeamId, TwoVsTwoMapId } from './types';

/**
 * Per-faction Core defense configuration (GDD §15). 'built-in' = the defense lives in
 * the shared combat paths (IC's Royal Ballista / Murder Slots / Last Course). Faction
 * blocks are implemented in sim/systems/coreDefense.ts as their factions land.
 */
export type CoreDefenseConfig =
  | { kind: 'built-in' }
  | {
      kind: 'ashen';
      shadeUnitId: string;
      shadeDuration: number;
      shadesPerMark: number;
      shadeCooldown: number;
      dreadPulseCooldown: number;
      dreadPulseRadius: number;
      throneFeastThreshold: number;
      throneFeastHealPerMark: number;
      throneFeastCooldown: number;
    }
  | {
      kind: 'verdant';
      rootSquadCap: number;
      rootDuration: number;
      rootCooldown: number;
      rootDps: number;
      barkRegenPerSecond: number;
    }
  | {
      kind: 'astral';
      repulsionCooldown: number;
      repulsionClusterSize: number;
      repulsionDistance: number;
      decoyThresholds: number[];
      decoyDuration: number;
      spellMantleCooldown: number;
      spellMantleDurationMult: number;
    };

export interface FactionDef {
  id: FactionId;
  name: string;
  /** false = data not yet authored; UI shows the tile disabled */
  implemented: boolean;
  coreDefId: string;
  siteTowerDefId: string;
  wallDefId: string;
  workerUnitId: string;
  /** worker shelter capacity inside the Core (GDD §11.8) */
  shelterCapacity: number;
  /** worker cap per zone (Core territory and each controlled site); workers stay in the zone they were trained in */
  workersPerZone: number;
  constructionModel: 'standard' | 'sacrificial' | 'growth' | 'phase';
  /** building def spawned while channeling a ritual (destructible standard) */
  ritualStandardDefId: string;
  /** deaths near this player's forces leave corpse marks (Ashen economy substrate) */
  corpseEconomy: boolean;
  coreDefense: CoreDefenseConfig;
  /** faction test deck (Bible §8); also the AI default */
  defaultDeck: string[];
  mark: string; // HUD faction mark
  palette: { primary: number; accent: number };
  uiBlurb: string;
}

export const FACTIONS: Record<FactionId, FactionDef> = {
  'iron-covenant': {
    id: 'iron-covenant',
    name: 'Iron Covenant',
    implemented: true,
    coreDefId: 'b-ic-crown-bastion',
    siteTowerDefId: 'b-ic-site-tower',
    wallDefId: 'b-ic-castle-wall',
    workerUnitId: 'u-ic-worker',
    shelterCapacity: 8,
    workersPerZone: 5,
    constructionModel: 'standard',
    ritualStandardDefId: 'b-ic-ritual-standard',
    corpseEconomy: false,
    coreDefense: { kind: 'built-in' },
    // Bible §8.1 "The Moving Fortress", trimmed to the 10-card deck (owner feedback);
    // slot order = hotkey order (one row of 10)
    defaultDeck: [
      'card-ic-barracks',
      'card-ic-foundry',
      'card-ic-builder-guild',
      'card-ic-vanguard',
      'card-ic-mobile-ballista',
      'card-ic-hold-the-line',
      'card-ic-minefield',
      'card-ic-arcturus',
      'card-ic-iron-saint',
      'card-ic-colossus',
    ],
    mark: '🦁',
    palette: { primary: 0x8fa8c8, accent: 0xd8b04a },
    uiBlurb:
      'Disciplined fortress warfare: armored infantry, repairable walls, and methodical siege engines. Strongest defenses in the war.',
  },
  'ashen-host': {
    id: 'ashen-host',
    name: 'Ashen Host',
    implemented: true,
    coreDefId: 'b-ah-ossuary-throne',
    siteTowerDefId: 'b-ah-site-tower',
    wallDefId: 'b-ah-castle-wall',
    workerUnitId: 'u-ah-worker',
    shelterCapacity: 6,
    workersPerZone: 5,
    constructionModel: 'sacrificial',
    ritualStandardDefId: 'b-ah-ritual-standard',
    corpseEconomy: true,
    coreDefense: {
      kind: 'ashen',
      shadeUnitId: 'u-ah-shade',
      shadeDuration: 18,
      shadesPerMark: 2,
      shadeCooldown: 12,
      dreadPulseCooldown: 30,
      dreadPulseRadius: 14,
      throneFeastThreshold: 0.35,
      throneFeastHealPerMark: 120, // bible AH-01
      throneFeastCooldown: 10,
    },
    defaultDeck: [
      'card-ah-grave-hatchery',
      'card-ah-blood-foundry',
      'card-ah-soul-mill',
      'card-ah-reavers',
      'card-ah-reckoning',
      'card-ah-black-harvest',
      'card-ah-night-unburied',
      'card-ah-veyr',
      'card-ah-colossus',
      'card-ah-hollow-king',
    ],
    mark: '💀',
    palette: { primary: 0x6a5d70, accent: 0x77d97e },
    uiBlurb:
      'Death is fuel: swarms that return from the grave, corpse-fed economy, and sacrifice that turns losses into power.',
  },
  'verdant-wilds': {
    id: 'verdant-wilds',
    name: 'Verdant Wilds',
    implemented: true,
    coreDefId: 'b-vw-heartroot',
    siteTowerDefId: 'b-vw-site-tower',
    wallDefId: 'b-vw-castle-wall',
    workerUnitId: 'u-vw-worker',
    shelterCapacity: 7,
    workersPerZone: 6, // largest-workforce identity: one extra worker per zone
    constructionModel: 'growth',
    ritualStandardDefId: 'b-vw-ritual-standard',
    corpseEconomy: false,
    coreDefense: {
      kind: 'verdant',
      rootSquadCap: 2,
      rootDuration: 6,
      rootCooldown: 14,
      rootDps: 18,
      barkRegenPerSecond: 0.0035, // bible VW-01: 0.35% max health per second
    },
    defaultDeck: [
      'card-vw-thorn-nursery',
      'card-vw-beast-hollow',
      'card-vw-living-grove',
      'card-vw-razorhorn',
      'card-vw-regrowth',
      'card-vw-stampede',
      'card-vw-awakening',
      'card-vw-thornback',
      'card-vw-ancient',
      'card-vw-titan',
    ],
    mark: '🌿',
    palette: { primary: 0x5f9e58, accent: 0xd8a44a },
    uiBlurb:
      'The forest fights back: the largest workforce, regenerating living structures, war beasts, and roots that swallow attackers.',
  },
  'astral-dominion': {
    id: 'astral-dominion',
    name: 'Astral Dominion',
    implemented: true,
    coreDefId: 'b-ad-meridian-prism',
    siteTowerDefId: 'b-ad-site-tower',
    wallDefId: 'b-ad-castle-wall',
    workerUnitId: 'u-ad-worker',
    shelterCapacity: 7,
    workersPerZone: 5,
    constructionModel: 'phase',
    ritualStandardDefId: 'b-ad-ritual-standard',
    corpseEconomy: false,
    coreDefense: {
      kind: 'astral',
      repulsionCooldown: 25,
      repulsionClusterSize: 3,
      repulsionDistance: 10,
      decoyThresholds: [0.7, 0.35],
      decoyDuration: 12,
      spellMantleCooldown: 45,
      spellMantleDurationMult: 0.5,
    },
    defaultDeck: [
      'card-ad-precision-foundry',
      'card-ad-conduit-forge',
      'card-ad-starforge-dock',
      'card-ad-blink-infantry',
      'card-ad-phase-lock',
      'card-ad-astral-barrage',
      'card-ad-convergence',
      'card-ad-xyra',
      'card-ad-arbiter',
      'card-ad-war-engine',
    ],
    mark: '✦',
    palette: { primary: 0x9fb8d8, accent: 0x6fd8e8 },
    uiBlurb:
      'Precision and displacement: teleporting strike forces, crystalline constructs, and a conduit network that bends space.',
  },
};

export function factionDef(id: FactionId): FactionDef {
  return FACTIONS[id];
}

/** Match setup: who plays which faction with which deck. */
export type AiDifficulty = 'easy' | 'standard' | 'hard';

export interface PlayerSetup {
  faction: FactionId;
  deck: string[];
  isAI: boolean;
  team: TeamId;
  /** Local skirmish AI tuning. Online human setups omit this field. */
  aiDifficulty?: AiDifficulty;
}

interface MatchSetupBase {
  /** Client-only guided match; the opposing army remains passive. */
  tutorial?: boolean;
}

export type MatchSetup =
  | (MatchSetupBase & { mode: '1v1'; mapId: OneVsOneMapId; players: [PlayerSetup, PlayerSetup] })
  | (MatchSetupBase & { mode: '2v2'; mapId: TwoVsTwoMapId; players: [PlayerSetup, PlayerSetup, PlayerSetup, PlayerSetup] });
